<template>
  <section>
    <div v-bind:id="editorId" class="base-editor" v-bind:class="{ 'show-placeholder': showPlaceHolder }" v-bind:style="disabled === true ? 'pointer-events: none;' : ''">
      {{ value }}
    </div>
    <div class="typography--caption typography--color-alert text-center pt-12" v-if="textLimitExceeded">
      {{ $t('editor.alerts.max_length', { n: textLimit }) }}
    </div>
  </section>
</template>

<script>
  import { mapActions } from 'vuex';

  export default {
    name: 'base-editor',

    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      editorId: {
        type: String,
        default: 'base-editor',
      },
      value: {
        type: String,
        default: '',
      },
      textLimit: {
        type: Number,
        default: 0,
      },
      placeholder: {
        type: String,
        default: null,
      },
      config: {
        type: Object,
        default() {
          return {};
        },
      },
    },

    watch: {
      value() {
        if (this.editor && this.placeholder && this.value !== this.editor.getContent() && this.value === '') {
          this.editor.setContent(this.placeholder);
          this.showPlaceHolder = true;
        } else if (this.editor && this.value !== this.editor.getContent({ format: 'raw' })) {
          this.editor.setContent(this.value, { format: 'raw' });
          this.showPlaceHolder = false;
        } else if (this.editor && this.value && this.value.indexOf('<img') === -1 && this.value.indexOf('<figure') !== -1) {
          this.editor.setContent(this.value.replace(/<figure>/g, '').replace(/<\/figure>/g, ''), { format: 'raw' });
        }
      },
    },

    data() {
      return {
        showPlaceHolder: true,

        textLimitExceeded: false,

        editor: null,

        editorConfig: {
          menubar: false,
          inline: true,
          theme: 'inlite',
          selection_toolbar: 'h1 blockquote | forecolor backcolor | charmap',
          insert_toolbar: '',
          plugins: [
            'autoresize lists hr textcolor charmap',
            'paste media table contextmenu',
          ],
          paste_as_text: true,

          toolbar: false,
          language_url: '/static/editor-langs/ko_KR.js',

          formats: {
            underline: { inline: 'u', exact: true },
          },

          extended_valid_elements: '*[*]',

          setup: (editor) => {
            editor.on('change', () => {
              const html = editor.getContent();
              const rawHtml = editor.getContent({ format: 'raw' });
              this.checkTextLimit(html);

              if (rawHtml !== this.value) {
                this.$emit('input', rawHtml);
              }
            });

            editor.on('NodeChange', () => {
              const html = editor.getContent();
              const rawHtml = editor.getContent({ format: 'raw' });
              this.checkTextLimit(html);

              if (rawHtml !== this.value) {
                this.$emit('input', rawHtml);
              }
            });

            editor.on('keyup', () => {
              const html = editor.getContent();
              const rawHtml = editor.getContent({ format: 'raw' });
              this.checkTextLimit(html);

              if (rawHtml !== this.value) {
                this.$emit('input', rawHtml);
              }
            });

            editor.on('SetContent', () => {
              const html = editor.getContent();
              const rawHtml = editor.getContent({ format: 'raw' });
              this.checkTextLimit(html);

              if (rawHtml !== this.value) {
                this.$emit('input', rawHtml);
              }
            });

            editor.on('MouseLeave', () => {
              this.setActiveEditorId(editor.id)
                .then(() => (
                  this.setActiveEditorLocation(editor.selection.getRng())
                ));
            });

            editor.on('Remove', () => {
              this.setActiveEditorId(null)
                .then(() => (
                  this.setActiveEditorLocation(null)
                ));
            });

            if (this.placeholder) {
              editor.on('focus', () => {
                setTimeout(() => {
                  if (editor.getContent() === `<p>${this.placeholder}</p>`) {
                    editor.setContent('');
                  }

                  this.showPlaceHolder = false;
                }, 0);
              });

              editor.on('blur', () => {
                this.setActiveEditorId(editor.id)
                  .then(() => {
                    if (editor.getContent() === '') {
                      editor.setContent(this.placeholder);
                      this.showPlaceHolder = true;
                    }
                  });
              });
            }
          },

          init_instance_callback: (editor) => {
            if ((this.value === '' || this.value === '<p><br data-mce-bogus="1"></p>') && this.placeholder) {
              this.showPlaceHolder = true;
              editor.setContent(this.placeholder);
            } else {
              this.showPlaceHolder = false;
            }

            this.editor = editor;
          },

          ...this.config,

          selector: `#${this.editorId}`,
        },
      };
    },

    methods: {
      ...mapActions([
        'setActiveEditorLocation',
        'setActiveEditorId',
      ]),

      // addScript(src, callback) {
      //   const existingScript = document.querySelector(`script[src="${src}"]`);
      //   if (existingScript) {
      //     if (callback) callback();
      //     return;
      //   }

      //   const script = document.createElement('script');
      //   script.src = src;
      //   script.defer = true;
      //   script.onload = () => {
      //     if (callback) callback();
      //   };
      //   script.onerror = () => {
      //     console.error(`Failed to load ${src}.`);
      //   };
      //   document.body.appendChild(script);
      // },

      remove() {
        if (this.editor) {
          this.editor.destroy();
        }
      },

      checkTextLimit(html) {
        if (this.textLimit === 0) {
          this.textLimitExceeded = false;
        } else {
          const text = html.replace(/(<([^>]+)>)/ig, '').replace(/&nbsp;/g, ' ').replace(/\u21B5/g, '\n');

          this.textLimitExceeded = text.length > this.textLimit;
          this.$emit('error', this.textLimitExceeded);
        }
      },
    },

    // classting@classting.com
    // lvcvmcr9jnarhlia2hga06vwilg5039i6bgwtxjtua5wkw9y

    // saffron3@classting.com
    // 4vmvo25qd25llbqbxcppndehf312a6610hdw1gd9b14m41yh

    // skswhwo@classting.com
    // 25436w1tw879ajrkc08ms9z3xu1tprwveiln2uv027pnte8q

    // skswhwo@gmail.com
    // 9or2mdqn9i1n6kbzcgn5ea0n15y4zufld99gbic70vp5hdsf

    // wwwjin015@gmail.com
    // 0fsxapejdhsx05mnnd3lpdak11luy89j7wfelyj5aqnew3ng

    mounted() {
      // const apiKey = process.env.NODE_ENV === 'production'
      //   ? '25436w1tw879ajrkc08ms9z3xu1tprwveiln2uv027pnte8q'
      //   : '0fsxapejdhsx05mnnd3lpdak11luy89j7wfelyj5aqnew3ng';
      // this.addScript(`https://cloud.tinymce.com/4.5.7/tinymce.min.js?apiKey=${apiKey}`, () => {
      //   const initTinyMCE = () => {
      //     if (typeof tinymce !== 'undefined') {
      //       tinymce.init(this.editorConfig);
      //     } else {
      //       setTimeout(initTinyMCE, 1000); // 1000ms 후에 다시 시도
      //     }
      //   };
      //   initTinyMCE();
      // });
      tinymce.init(this.editorConfig);
    },

    beforeDestroy() {
      this.remove();
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/BaseEditor.vue